import dayjs from 'dayjs';

import { getWordpress } from 'PublicAPI';

import { GUIDE_SUMMARY_KEYS } from './wordpress/@types/guide';

export const WPENGINE_BASE_URL = '/api/wpengine'; // wpengine proxy for cache

export const SELL_MY_CAR_PATH = '/sell-my-car';
export const SELL_MY_VAN_PATH = '/sell-my-van';

export const SELL_MY_CAR_LANDING_PAGE_V2 = `${SELL_MY_CAR_PATH}/lp`;

export const GUIDES_PATH = '/sell-my-car/guides';

export const CONTACT_PATH = '/contact';

export const BLOG_PATH = '/blog';

export const REFACTORED_LANDING_PAGE_ROUTES = Object.freeze([
  SELL_MY_CAR_PATH,
  SELL_MY_VAN_PATH,
]);

export const LANDING_PAGE_ROUTES = Object.freeze([
  `${SELL_MY_CAR_PATH}/:vehicle?-buyers`,
  `${SELL_MY_CAR_PATH}/car-buying-sites`,
  `${SELL_MY_CAR_PATH}/car-valuation`,
  `${SELL_MY_CAR_PATH}/car-buyers`,
  `${SELL_MY_CAR_PATH}/cash-for-:vehicle?`,
  `${SELL_MY_CAR_PATH}/scrap-my-car`,
  `${SELL_MY_CAR_PATH}/buy-my-car`,
  `${SELL_MY_CAR_PATH}/to-a-dealer`,
  `${SELL_MY_CAR_PATH}/on-finance`,
  `${SELL_MY_CAR_PATH}/diesel`,
  `${SELL_MY_CAR_PATH}/electric`,
  `${SELL_MY_CAR_PATH}/hybrid`,
  `${SELL_MY_CAR_PATH}/petrol`,
  `${SELL_MY_CAR_PATH}/suv`,
  `${SELL_MY_CAR_PATH}/on-probate`,
  `${SELL_MY_CAR_PATH}/moneysupermarket`,
  `${SELL_MY_CAR_PATH}/podcasts`,
  `${SELL_MY_CAR_PATH}/quidco`,
  `${SELL_MY_CAR_PATH}/topcashback`,
  `${SELL_MY_CAR_PATH}/giveasyoulive`,
  `${SELL_MY_CAR_PATH}/easyfundraising`,
  `${SELL_MY_CAR_PATH}/kidstart`,
  `${SELL_MY_CAR_PATH}/refer`,
  `${SELL_MY_CAR_PATH}/ulez`,
  SELL_MY_CAR_LANDING_PAGE_V2,
]);

export const VALUATION_TRACKER_ROUTES = Object.freeze([
  '/car-value-tracker',
]);

export const NO_SITEMAP_ROUTES = Object.freeze([
  `${SELL_MY_CAR_PATH}/suv`,
  `${SELL_MY_CAR_PATH}/moneysupermarket`,
  `${SELL_MY_CAR_PATH}/podcasts`,
  `${SELL_MY_CAR_PATH}/quidco`,
  `${SELL_MY_CAR_PATH}/topcashback`,
  `${SELL_MY_CAR_PATH}/car-valuation`,
  `${SELL_MY_CAR_PATH}/giveasyoulive`,
  `${SELL_MY_CAR_PATH}/easyfundraising`,
  `${SELL_MY_CAR_PATH}/kidstart`,
  `${SELL_MY_CAR_PATH}/ulez`,
  `${SELL_MY_CAR_PATH}/refer`,
  SELL_MY_CAR_LANDING_PAGE_V2,
  ...VALUATION_TRACKER_ROUTES,
  '/verify',
]);

export const ULEZ_PAGE_ROUTES = Object.freeze([
  '/ulez-checker',
  '/ulez-checker/:results',
]);

const WORDPRESS_ROUTES_CONFIG = Object.freeze([
  { path: `${SELL_MY_CAR_PATH}/guides/:subdir?`, section: 'guides', zendeskWidget: true },
  { path: '/blog/:year?/:month?/:day?/:post?', section: 'blog', zendeskWidget: true },
  { path: '/press', section: 'press', zendeskWidget: true },
  { path: '/cookies', section: 'cookies' },
  { path: '/privacy', section: 'privacy' },
  { path: '/terms', section: 'terms' },
  { path: '/claim', section: 'claim' },
]);

export const NEW_WORDPRESS_PAGE_ROUTES = Object.freeze([
  `${SELL_MY_CAR_PATH}/guides`,
  ...WORDPRESS_ROUTES_CONFIG.map((config) => config.path),
].map((route) => `/wp${route}`)); // TODO - remove `/wp` after testing

export const WORDPRESS_ROUTES = Object.freeze([
  ...WORDPRESS_ROUTES_CONFIG,
  ...WORDPRESS_ROUTES_CONFIG.map((config) => ({ ...config, noRobots: true, path: `/wp${config.path}` })),
]); // TODO - delete WORDPRESS_ROUTES after testing and export WORDPRESS_ROUTES_CONFIG

export const FULL_LANDING_PAGE_ROUTES = Object.freeze([
  ...LANDING_PAGE_ROUTES,
  ...ULEZ_PAGE_ROUTES,
  ...VALUATION_TRACKER_ROUTES,
  ...REFACTORED_LANDING_PAGE_ROUTES,
]);

export default {
  // Making this into shorthand makes `this` into undefined so `this.getPage` etc won't work
  // eslint-disable-next-line func-names, object-shorthand
  fetchBlogData: async function (wordpressApi, urlParts) {
    const getExcerpt = (post) => {
      let exc = post.excerpt.rendered;
      if (!exc) {
        return post.content.rendered;
      }

      const len = wordpressApi.length;
      const url = wordpressApi;
      const link = post.link.replace(url[len - 1] === '/' ? url.substr(0, len - 1) : url, '');

      exc = exc.replace(/\n<p\sclass.*link-more.*p>\n/g, '');
      exc += `\n<p class="link-more"><a href="${link}" class="mw-link mw-link-blue mw-link-small mw-link-copy">Continue reading</a></p>\n`;
      return exc;
    };

    const isNumber = (v) => !Number.isNaN(v) && Number.isInteger(parseInt(v));

    if (urlParts.includes('blog')) {
      urlParts = urlParts.slice(urlParts.indexOf('blog') + 1);
    }

    let params = {};

    const wp = {
      archive: null,
      content: null,
      meta: {
        desc: null,
        title: null,
      },
      posts: [],
      showFull: false,
      title: null,
    };

    if (urlParts.length === 0) {
      // Base view
      params.per_page = 10;
    } else if (urlParts.length < 4) {
      if (!urlParts.slice(0, 3).every(isNumber)) {
        return;
      }

      let period = 'year';

      const formatDate = (date, end) => {
        date = +date;
        return (!Number.isNaN(date) && date > 0 && date <= end) ? date.toString().padStart(2, 0) : '01';
      };

      const daysInMonth = dayjs(`${+urlParts[0] || dayjs().year()}-${formatDate(urlParts[1], 12)}`, 'YYYY-MM').daysInMonth();

      if (urlParts[2]) {
        /**
         * Stop the server from falling over on `https://motorway.co.uk/blog/2017/02/31`
         * This is also dealt with in the router
         */
        if (!Number.isNaN(+urlParts[2]) && +urlParts[2] > daysInMonth) {
          urlParts[2] = daysInMonth;
        }

        period = 'day';
      } else if (urlParts[1]) {
        period = 'month';
      }

      const date = dayjs.utc([
        +urlParts[0] || dayjs().year(), formatDate(urlParts[1], 12), formatDate(urlParts[2], 31),
      ].join('-'));

      const startDate = date.startOf(period).toISOString().slice(0, -5);
      const endDate = date.endOf(period).toISOString().slice(0, -5);

      params = {
        after: startDate,
        before: endDate,
      };
    } else {
      // Single post
      params.slug = urlParts[urlParts.length - 1];
    }

    const body = await this.getBlogPosts(Object.keys(params).map((x) => `${x}=${params[x]}`).join('&'));

    if (!body.length) {
      return wp; // eslint-disable-line consistent-return
    }

    if (params.slug) {
      wp.showFull = true;
    }

    wp.posts = body.map((p) => {
      const date = new Date(p.date);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, 0);
      const day = date.getDate().toString().padStart(2, 0);

      if (params.slug) {
        wp.meta = {
          desc: p.yoast.yoast_wpseo_metadesc,
          title: p.yoast.yoast_wpseo_title,
        };
      }

      return {
        // eslint-disable-next-line no-underscore-dangle
        author: p._embedded.author[0],
        content: p.content.rendered,
        date: dayjs(p.date).format('Do MMMM YYYY'),
        excerpt: getExcerpt(p),
        slug: params.slug ? null : `/blog/${year}/${month}/${day}/${p.slug}`,
        title: p.title.rendered,
      };
    });

    const archive = [];
    const aBody = await this.getArchive();

    if (aBody && aBody.length > 0) {
      aBody.forEach((x) => {
        const date = dayjs(x, 'MMMM YYYY');
        archive.push({
          label: x,
          route: `/blog/${date.year()}/${(date.month() + 1).toString().padStart(2, 0)}`,
        });
      });

      wp.archive = archive;
    }

    return wp; // eslint-disable-line consistent-return
  },
  // eslint-disable-next-line func-names, object-shorthand
  fetchLandingPage: async function (slug) {
    const body = await this.getPage(slug);

    const wp = {
      archive: null,
      content: null,
      meta: {
        desc: null,
        title: null,
      },
      posts: [],
      title: null,
    };

    if (!body[0]) {
      return wp;
    }

    const { acf } = body[0];
    wp.title = acf.header_title || '';
    wp.meta.title = acf.seo_title || wp.title;
    wp.meta.description = acf.seo_description || acf.header_description || '';
    wp.meta.desc = wp.meta.description;
    wp.content = acf;

    return wp;
  },
  // eslint-disable-next-line func-names, object-shorthand
  fetchPageData: async function (slug, isGuideRoute, isTestingWpEngineAPI = false, headers = null) {
    const body = await this.getPage(slug, isGuideRoute, isTestingWpEngineAPI, headers); // https://github.com/motorway/motorway-seller-webapp#wordpress-content
    const wp = {
      archive: null,
      content: null,
      meta: {
        desc: null,
        title: null,
      },
      posts: [],
      title: null,
    };

    if (body.length > 0) {
      const data = body[0];

      wp.title = data.title.rendered;
      wp.content = data.content.rendered;
      wp.meta = {
        desc: data.yoast.yoast_wpseo_metadesc,
        published: data.date,
        title: data.yoast.yoast_wpseo_title,
      };
    }
    return wp;
  },
  // eslint-disable-next-line func-names, object-shorthand
  getArchive: async function () {
    return getWordpress('/serve_archive/archive');
  },
  // eslint-disable-next-line func-names, object-shorthand
  getBlogPosts: async function (params) {
    return getWordpress(`/wp/v2/posts?_embed&${params}`);
  },

  async getGuidesPreviewData() {
    const guideData = getWordpress('/wp/v2/pages/?parent=32');
    return guideData.then((guides) =>
      guides.map((guide) =>
        Object.fromEntries(Object.entries(guide).filter(([k]) => GUIDE_SUMMARY_KEYS.includes(k)))));
  },
  // eslint-disable-next-line func-names, object-shorthand
  getPage: async function (slug, isGuideRoute, isTestingWpEngineAPI = false, headers = null) {
    const guide = (isGuideRoute) ? '&parent=32' : '';
    return getWordpress(`/wp/v2/pages/?slug=${slug}${guide}`, isTestingWpEngineAPI, headers);
  },
};

// Temporary testing of WP Engine API for selected guide pages on Staging
export const TEST_SLUG_PREFIX = '/wp/sell-my-car/guides/';
export const TEST_SLUGS = [
  'top-10-tips-selling-car',
  'how-to-sell-a-car-privately',
  'buy-my-car-best-ways-to-sell-car',
];
